import { darken, lighten } from '@mui/material/styles';

/**
 * Given a hex returns the color on rgb format with the indicated alpha
 * ie: #757774, 0.5 => rgb(117,119,116, 0.5)
 * @param  h hex code
 * @param  opacity alpha value
 */
export const hexToRGB = (h: string, opacity: number | null = null) => {
  let r: string | number = 0;
  let g: string | number = 0;
  let b: string | number = 0;

  if (h.length === 4) {
    // 3 digits
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;
  } else if (h.length === 7) {
    // 6 digits stripping the #
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }

  const rgb = `${+r},${+g},${+b}`;

  return !opacity ? `rgb(${rgb})` : `rgba(${rgb}, ${opacity})`;
};

/**
 * Given a rgb modify the opacity value for a given one
 * @param rgb ie: rgb(117,119,116, 0.5), 0.1 => rgb(117,119,116, 0.1)
 * @param  opacity
 * @return rgba color
 */
export const rgbToAlpha = (rgb: string, opacity: number) => {
  const colorComp = rgb.substring(0, rgb.length - 1).split(',');
  if (colorComp.length === 0) {
    throw new Error('Invalid color format');
  }

  let newRgb = '';
  for (let i = 0; i < 3; i++) {
    newRgb += `${colorComp[i]},`;
  }

  newRgb += `${opacity})`;
  return newRgb;
};

const augmentColor = (main: string) => ({
  main,
  light: lighten(main, 0.6),
  dark: darken(main, 0.15)
});

// our current background.main is the lightest of the 3
const augmentBackgroundColor = (main: string) => ({
  main,
  light: darken(main, 0.05),
  dark: darken(main, 0.2)
});

export const createColorPalette = ({
  primaryMain,
  backgroundsMain,
  typographyMain,
  controlsMain,
  errorMain
}: {
  primaryMain: string;
  backgroundsMain: string;
  typographyMain: string;
  controlsMain: string;
  errorMain: string;
}) => {
  const primaryColors = augmentColor(primaryMain);
  const backgroundsColors = augmentBackgroundColor(backgroundsMain);
  const typographyColors = augmentColor(typographyMain);
  const controlsColors = augmentColor(controlsMain);
  const errorColors = augmentColor(errorMain);

  return {
    backgrounds: {
      ...backgroundsColors,
      lightContrast: darken(backgroundsColors.light, 0.05),
      error: darken(errorColors.main, 0.2),
      errorAlternate: lighten(errorColors.light, 0.7),
      modal: backgroundsColors.main
    },
    primary: primaryColors,
    error: errorColors,
    typography: { ...typographyColors, light: darken(typographyColors.light, 0.2) },
    controls: controlsColors,
    action: {
      hover: lighten(primaryColors.light, 0.8),
      selected: backgroundsColors.main,
      disabledBackground: hexToRGB(primaryColors.main, 0.6)
    }
  };
};
